import { useCallback, useEffect, useMemo, useState } from 'react';
import getLang from 'LearningCenterUI/utils/getLang';
import shuffle from 'hs-lodash/shuffle';
import Raven from 'raven-js';
import { useGenerateQuizMeTrackIds, useAsyncGetLazyPollingQuizMe } from '../queries/quizMeQueries';
import { useQuizMeSession } from '../utils/quizMeUtils';
import UsageTracker from '../../../utils/UsageTracker';
import { useShort } from '../../shortContent';
import { useFullLesson } from '../../lessons/lessonQueries';
import { useAsyncBulkGetQuizMeQuery } from '../../../../__generated__/chirp/com/hubspot/academy/gen/ai/skills/rpc/QuizMeRpc.hooks';
import { GET_VIDEO_CAPTION_DATA, getVideoCaptionData, useVideoPlayerCaptions } from '../../video/videoHooks';
import { updateCache } from '../../../utils/DataFetchingClientCache';
import { useGetAiAccessControls } from '../../aiControl/aiAccessQueries';
import { isAcceptanceTest } from 'hub-http/helpers/env';
// TODO: Remove shuffle once backend implements randomisation
const shuffleTaskAnswers = task => {
  var _task$quizResponse;
  if (!(task !== null && task !== void 0 && (_task$quizResponse = task.quizResponse) !== null && _task$quizResponse !== void 0 && _task$quizResponse.quiz)) return task;
  return Object.assign({}, task, {
    quizResponse: Object.assign({}, task.quizResponse, {
      quiz: Object.assign({}, task.quizResponse.quiz, {
        questions: task.quizResponse.quiz.questions.map(question => Object.assign({}, question, {
          answers: shuffle(question.answers)
        }))
      })
    })
  });
};
export const useAsyncGetQuizMeFromCache = ({
  primaryContentId,
  contentType,
  lessonContentId,
  language
}) => {
  const isShorts = contentType === 'SHORT_FORM_CONTENT';
  const isLesson = contentType === 'LESSON';
  const {
    short
  } = useShort(primaryContentId, !isShorts);
  const {
    lesson
  } = useFullLesson(Number(primaryContentId), language || getLang(), !isLesson, 'cache-only');
  const videoContents = useMemo(() => {
    return lesson === null || lesson === void 0 ? void 0 : lesson.content.filter(content => content.contentType === 'VIDEO');
  }, [lesson]);
  const contentIds = useMemo(() => {
    if (isLesson && videoContents) {
      return videoContents.map(content => Number(content.id));
    }
    if (isShorts) {
      return [Number(short === null || short === void 0 ? void 0 : short.reference)];
    }
    return [];
  }, [videoContents, short, isLesson, isShorts]);
  const {
    getQuizState
  } = useQuizMeSession();
  const taskIds = useMemo(() => {
    if (isLesson) {
      return contentIds.map(contentId => {
        var _getQuizState;
        return (_getQuizState = getQuizState(contentId, contentType)) === null || _getQuizState === void 0 ? void 0 : _getQuizState.tasks;
      }).filter(task => task !== undefined);
    }
    if (isShorts) {
      var _getQuizState2;
      return [(_getQuizState2 = getQuizState(Number(primaryContentId), contentType)) === null || _getQuizState2 === void 0 ? void 0 : _getQuizState2.tasks].filter(task => task !== undefined);
    }
    return [];
  }, [contentIds, contentType, getQuizState, isLesson, isShorts, primaryContentId]);
  const {
    data
  } = useAsyncBulkGetQuizMeQuery({
    variables: {
      taskIds
    },
    fetchPolicy: 'cache-only',
    skip: isAcceptanceTest()
  });
  const quizMeData = useMemo(() => {
    if (isLesson) {
      const lessonContentIndex = lesson === null || lesson === void 0 ? void 0 : lesson.content.filter(content => content.contentType === 'VIDEO').findIndex(content => content.id === lessonContentId);
      const task = lessonContentIndex !== undefined ? data === null || data === void 0 ? void 0 : data.tasks[lessonContentIndex] : undefined;
      return shuffleTaskAnswers(task);
    }
    if (isShorts) return shuffleTaskAnswers(data === null || data === void 0 ? void 0 : data.tasks[0]);
    return undefined;
  }, [isLesson, isShorts, lesson === null || lesson === void 0 ? void 0 : lesson.content, data, lessonContentId]);
  return quizMeData;
};
const useGetShortsQuizMeRequestParams = content => {
  const {
    contentPortalId,
    outputLanguage,
    videoId
  } = useMemo(() => ({
    contentPortalId: content !== null && content !== void 0 && content.hubId ? content.hubId : undefined,
    outputLanguage: content !== null && content !== void 0 && content.language ? content.language.toUpperCase() : undefined,
    videoId: content !== null && content !== void 0 && content.reference ? Number(content === null || content === void 0 ? void 0 : content.reference) : undefined
  }), [content]);
  const {
    captionsAvailable
  } = useVideoPlayerCaptions(videoId, content === null || content === void 0 ? void 0 : content.contentHublet, content === null || content === void 0 ? void 0 : content.hubId);
  return contentPortalId && outputLanguage && videoId && captionsAvailable ? [{
    contentPortalId,
    outputLanguage,
    videoId
  }] : undefined;
};
//To check if captions are available for all videos within a lesson
const getCaptionsForAllVideos = async videos => {
  return await Promise.allSettled(videos.map(video => {
    if (!video.reference || !video.hublet || !video.hubId) {
      return Promise.reject(new Error(`Missing video metadata for video content${video.id}`));
    }
    return getVideoCaptionData(Number(video.reference), video.hublet, video.hubId);
  })).then(results => {
    const errors = [];
    const successfulResults = results.map((result, index) => {
      if (result.status === 'fulfilled') {
        return Object.assign({}, videos[index], {
          captions: result.value
        });
      } else {
        const error = result.reason instanceof Error ? result.reason : new Error(String(result.reason));
        errors.push({
          error,
          video: videos[index]
        });
        return null;
      }
    }).filter(item => item !== null);
    if (errors.length > 0) {
      errors.forEach(({
        error,
        video
      }) => {
        Raven.captureException(error, {
          extra: {
            videoId: Number(video.reference)
          }
        });
      });
    }
    return successfulResults;
  });
};
const useGetLessonQuizMeRequestParams = lesson => {
  const [responseWithCaptions, setResponseWithCaptions] = useState([]);
  useEffect(() => {
    if (!lesson || !(lesson !== null && lesson !== void 0 && lesson.content.length)) return;
    const videos = lesson.content.filter(content => content.contentType === 'VIDEO');
    let cancelled = false;
    void getCaptionsForAllVideos(videos).then(data => {
      if (cancelled) return;
      const requests = data.map(content => {
        updateCache(GET_VIDEO_CAPTION_DATA, () => content.captions, {
          videoId: Number(content.reference),
          creatorRegion: content.hublet,
          creatorHubId: content.hubId
        });
        return {
          contentPortalId: content.hubId,
          videoId: Number(content.reference),
          lessonContentId: Number(content.id),
          outputLanguage: content.language.toUpperCase()
        };
      });
      setResponseWithCaptions(requests);
    });
    return () => {
      cancelled = true;
    };
  }, [lesson]);
  return responseWithCaptions.length > 0 ? responseWithCaptions : undefined;
};
export const useGenerateAsyncQuizMe = ({
  primaryContentId,
  contentType,
  lessonContentId,
  interactiveContentTrackingProps,
  language,
  triggerGeneration = true
}) => {
  const {
    aiAccessControls
  } = useGetAiAccessControls();
  const isGenerativeAiEnabled = !!(aiAccessControls !== null && aiAccessControls !== void 0 && aiAccessControls.generativeAiEnabled);
  const {
    getQuizState,
    initialiseQuiz,
    setQuizMeTasks
  } = useQuizMeSession();
  const quizState = getQuizState(lessonContentId || primaryContentId, contentType);
  const taskIds = Object.values((quizState === null || quizState === void 0 ? void 0 : quizState.tasks) || {});
  const isShorts = contentType === 'SHORT_FORM_CONTENT';
  const isLesson = contentType === 'LESSON';

  // Fetch content based on type
  const {
    short
  } = useShort(primaryContentId, !isShorts);
  const {
    lesson
  } = useFullLesson(Number(primaryContentId), language || getLang(), !isLesson);
  const lessonQuizMeRequestParams = useGetLessonQuizMeRequestParams(lesson);
  const shortsQuizMeRequestParams = useGetShortsQuizMeRequestParams(short);
  const contentIds = useMemo(() => {
    if (isLesson) {
      return (lessonQuizMeRequestParams === null || lessonQuizMeRequestParams === void 0 ? void 0 : lessonQuizMeRequestParams.map(request => request.lessonContentId).filter(id => id !== undefined)) || [];
    }
    if (isShorts && primaryContentId) {
      return [primaryContentId];
    }
    return [];
  }, [isLesson, isShorts, lessonQuizMeRequestParams, primaryContentId]);
  const onAsyncQuizMeGenerationCompleted = useCallback(data => {
    if (!quizState || !quizState.tasks) {
      if (isLesson && lessonQuizMeRequestParams) {
        lessonQuizMeRequestParams.forEach(request => {
          initialiseQuiz({
            contentType,
            contentId: request.lessonContentId
          });
        });
      }
      if (isShorts) {
        initialiseQuiz({
          contentType,
          contentId: primaryContentId
        });
      }
      data.result.tasks.forEach((task, index) => {
        if (lessonQuizMeRequestParams) {
          setQuizMeTasks({
            contentType,
            videoContentId: lessonQuizMeRequestParams[index].lessonContentId || primaryContentId,
            tasks: task.taskId
          });
        }
        if (shortsQuizMeRequestParams) {
          setQuizMeTasks({
            contentType,
            videoContentId: primaryContentId,
            tasks: task.taskId
          });
        }
      });
      if (interactiveContentTrackingProps) {
        UsageTracker.trackStandalone('Learning Center Interaction', Object.assign({}, interactiveContentTrackingProps, {
          action: 'ai-quiz-me-generated-quiz'
        }));
      }
    }
  }, [primaryContentId, contentType, initialiseQuiz, interactiveContentTrackingProps, isLesson, isShorts, lessonQuizMeRequestParams, quizState, setQuizMeTasks, shortsQuizMeRequestParams]);
  const {
    getQuizMeLazy,
    quizMeError,
    quizMeLoading: quizMeLoadingQuery,
    queryLoading: quizMeQueryLoading,
    retryGeneration
  } = useAsyncGetLazyPollingQuizMe({
    contentIds,
    contentType
  });
  const {
    asyncGenerateQuizMe,
    generateQuizTrackIdsError,
    generateQuizTrackIds,
    generateQuizTrackIdsLoading
  } = useGenerateQuizMeTrackIds({
    onCompleted: quizData => {
      onAsyncQuizMeGenerationCompleted(quizData);
      const _taskIds = quizData.result.tasks.map(task => task.taskId);
      void getQuizMeLazy({
        variables: {
          taskIds: _taskIds
        }
      });
    }
  });
  const quizMeRequestParams = useMemo(() => {
    return shortsQuizMeRequestParams || lessonQuizMeRequestParams || undefined;
  }, [shortsQuizMeRequestParams, lessonQuizMeRequestParams]);
  const handleTriggerAsyncQuizGeneration = useCallback(() => {
    if (quizMeRequestParams && !isAcceptanceTest()) {
      void asyncGenerateQuizMe({
        variables: {
          videos: quizMeRequestParams
        }
      });
    }
  }, [asyncGenerateQuizMe, quizMeRequestParams]);
  const onAsyncRegenerateQuiz = useCallback(() => {
    if (taskIds.length === 0) {
      handleTriggerAsyncQuizGeneration();
    }
    setQuizMeTasks({
      contentType,
      videoContentId: lessonContentId || primaryContentId,
      tasks: undefined
    });
  }, [taskIds.length, setQuizMeTasks, contentType, lessonContentId, primaryContentId, handleTriggerAsyncQuizGeneration]);
  const shouldTriggerQuizGeneration = useMemo(() => triggerGeneration && !generateQuizTrackIdsLoading && !generateQuizTrackIdsError && !generateQuizTrackIds, [generateQuizTrackIds, generateQuizTrackIdsError, generateQuizTrackIdsLoading, triggerGeneration]);
  useEffect(() => {
    if ((shouldTriggerQuizGeneration || retryGeneration) && !!isGenerativeAiEnabled) {
      handleTriggerAsyncQuizGeneration();
    }
  }, [aiAccessControls, handleTriggerAsyncQuizGeneration, isGenerativeAiEnabled, retryGeneration, shouldTriggerQuizGeneration]);
  return {
    asyncGenerateQuizMe,
    onAsyncRegenerateQuiz,
    generateQuizError: generateQuizTrackIdsError || quizMeError,
    generateQuizLoading: generateQuizTrackIdsLoading || quizMeLoadingQuery || quizMeQueryLoading
  };
};